<template>
  <v-main>
    <div v-if="currentUser.is_faculty || show_result">
      <v-row v-if="test" class="test-info">
        <v-col cols="6" class="test-details">
          <h2>{{ test.tname }}</h2>
          <h4>{{ test.subject.name }}</h4>
          <h4>{{ test.subject.room.sections }}</h4>

          <p class="description-text">
            {{ startDateTime }} - {{ endDateTime }}
          </p>
          <h2 v-if="studentDetails">{{ studentDetails.full_name }}</h2>
        </v-col>
        <v-col cols="6" class="align-end">
          <p>
            <v-btn
            id="all-results-button"
              v-if="!currentUser.is_student"
              :to="`/test/${$route.params.tid}/stats`"
              >All Results</v-btn
            >
          </p>
          <p>
            <v-btn
              @click="createPDF"
              id="download-student-result-button"
              color="primary"
              class="pdf-btn"
              >Download</v-btn
            >
          </p>
        </v-col>
      </v-row>

      <v-card class="student_result">
        <v-card-title>
          <b v-if="total != null && scored != null"
            >Marks Scored: {{ scored }}/{{ total }}</b
          >
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <v-data-table
          ref="studentResultTable"
          :headers="headers"
          :items="results"
          :search="search"
          :expanded="currentUser.is_faculty"
          :show-expand="currentUser.is_faculty"
          :items-per-page="-1"
        >
          <template v-slot:item.correct="{ item }">
            <div v-if="!item.is_subjective">
              <span>
                <i
                  class="fas fa-check pass"
                  v-if="item.is_correct && !item.answer"
                ></i>
              </span>
              <span>
                <i
                  class="fas fa-times fail"
                  v-if="!item.is_correct && !item.answer"
                ></i>
              </span>
            </div>
          </template>
          <template v-slot:item.marks="{ item }">
            <span v-if="item.is_correct && !item.answer" class="pass">{{
              getMarks(item)
            }}</span>
            <span v-else-if="item.answer">{{ getMarks(item) }}</span>
            <span v-else class="fail">{{ getMarks(item) }}</span>
          </template>
          <template
            v-if="!currentUser.is_student"
            v-slot:item.data-table-expand="{ item, isExpanded, expand }"
          >
            <v-btn
              @click="expand(true)"
              v-if="item.answer && !isExpanded"
              small
              color="primary"
              >Evaluate</v-btn
            >
            <v-btn
              @click="
                () => {
                  expand(false);
                  updateMarks(item);
                }
              "
              v-if="item.answer && isExpanded"
              small
              >Done</v-btn
            >
          </template>
          <template
            v-if="!currentUser.is_student"
            v-slot:expanded-item="{ headers, item }"
          >
            <td :colspan="headers.length">
              <v-row justify="center">
                <v-col lg="6" md="8" cols="12">
                  <div class="text-center mt-4 marks-slider">
                    <v-slider
                      v-if="item.question.is_subjective && item.answer"
                      :max="item.question.marks"
                      v-model="item.answer.marks"
                      thumb-label="always"
                      append-icon="fas fa-plus"
                      @click:append="item.answer.marks++"
                      prepend-icon="fas fa-minus"
                      @click:prepend="item.answer.marks--"
                    ></v-slider>
                  </div>
                </v-col>
              </v-row>

              <p v-if="item.question.is_subjective && item.answer">
                {{ item.answer.student_ans }}
              </p>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <div
      v-if="currentUser.is_student && !show_result && studentDetails != null"
    >
      <v-container>
        <h1>Results Cannot Be Shown</h1>
      </v-container>
    </div>
  </v-main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Helper from "@utils/misc";
import testsApi from "@api/tests/all_tests";
import api from "@api/index";
import moment from "moment";
import endpoints from "@api/repository";

import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: "StudentResult",
  data() {
    return {
      studentDetails: null,
      search: "",
      expanded: [],
      headers: [
        {
          text: "Question",
          align: "start",
          sortable: false,
          value: "question.qtext",
        },
        { text: "Correct Answer", value: "correct_ans.op" },
        { text: "Student Answer", value: "student_ans.op" },
        { text: "Correct", value: "correct" },
        { text: "Marks Scored", value: "marks" },
        { text: "", align: "center", value: "data-table-expand" },
      ],
      test: null,
      results: [],
      scored: null,
      total: null,
      show_result: false,
    };
  },
  computed: {
    ...mapGetters(["isLoading", "currentUser", "accessToken"]),
    startDateTime: function () {
      var d = new Date(this.test.start_datetime * 1000);
      return moment(d).format("D MMM h:mma");
    },
    endDateTime: function () {
      var d = new Date(this.test.end_datetime * 1000);
      return moment(d).format("D MMM h:mma");
    },
    apiEssentials() {
      return {
        accessToken: this.accessToken,
        setLoading: this.setLoading,
        handleErrors: this.handleApiErrors,
      };
    },
  },
  methods: {
    ...mapActions(["setLoading"]),
    // gets the test details for displaying
    async getTestDetails() {
      this.setLoading(true);
      // gets test details
      let testFromServer = await testsApi.getTestDetails(
        this.accessToken,
        this.$route.params.tid
      );
      if (!Helper.handleErrors(testFromServer, this.handleErrors)) return;
      // display
      this.test = testFromServer;
      this.setLoading(false);
    },
    // get student results of a test
    async getStudentResults() {
      this.setLoading(true);
      var studentId =
        this.$route.params.sid == "None"
          ? this.currentUser.id
          : this.$route.params.sid;
      let finalResultFromServer = await testsApi.getStudentResultsFromServer(
        this.accessToken,
        this.$route.params.tid,
        studentId
      );

      if (!Helper.handleErrors(finalResultFromServer, this.handleApiErrors))
        return false;

      this.results = finalResultFromServer.results;
      this.studentDetails = finalResultFromServer.student;
      this.scored = finalResultFromServer.scored;
      this.total = finalResultFromServer.total;
      this.show_result = finalResultFromServer.show_result;
      this.setLoading(false);
    },
    handleApiErrors(err) {
      console.log(err);
      this.setLoading(false);
      if (err.response.status == 405) {
        this.$router.push("/restricted");
      }
    },
    getMarks(item) {
      if (item.answer) {
        return `${item.answer.marks}/${item.question.marks}`;
      }
      return (
        (item.is_correct ? item.question.marks : "0") +
        "/" +
        item.question.marks
      );
    },
    //Update subjective marks
    async updateMarks(item) {
      this.setLoading(true);
      var response;
      response = await api.handleApi({
        apiEssentials: this.apiEssentials,
        url: endpoints.updateSubjectiveMarks,
        responseVar: response,
        method: api.Methods.POST,
        body: item,
      });
      this.scored = response.scored;
      this.total = response.total;
      this.setLoading(false);
    },

    hideElementsForPrinting() {
      document.getElementById('app-bar-toolbar').style.display = "none";

      document.getElementById("download-student-result-button").style.display =
        "none";
        document.getElementById("all-results-button").style.display =
        "none";
    },

    showElementsForPrinting() {
      document.getElementById('app-bar-toolbar').style.display = "block";

      document.getElementById("download-student-result-button").style.display =
        "inline";
         document.getElementById("all-results-button").style.display =
        "inline-flex";
    },

    async createPDF() {
      this.hideElementsForPrinting();
      print();
      this.showElementsForPrinting();
    },

    // createPDF() {
    //   this.setLoading(true);
    //   var source = this.$refs["studentResultTable"];
    //   let rows = [];
    //   let columnHeader = [
    //     "Question",
    //     "Correct Answer",
    //     "Student Answer",
    //     "Correct",
    //     "Marks",
    //   ];
    //   let pdfName = `Result_${this.studentDetails.full_name}`;
    //   source.items.forEach((element) => {
    //     var temp = [
    //       element.question.qtext,
    //       element.correct_ans?.op ?? "*SUBJECTIVE",
    //       element.student_ans?.op ?? element?.answer?.student_ans ?? "-",
    //       element.answer ? "-" : element.is_correct ? "Correct" : "Incorrect",
    //       this.getMarks(element),
    //     ];
    //     rows.push(temp);
    //   });
    //   var doc = new jsPDF();

    //   // Student header
    //   let detailsHeader = ["Student Name", "Test Name", "Subject", "Marks"];
    //   var detailRow = [
    //     this.studentDetails.full_name,
    //     this.test.tname,
    //     `${this.test.subject.name}-${this.test.subject.room.sections}`,
    //     `${this.scored}/${this.total}`,
    //   ];
    //   doc.autoTable(detailsHeader, [detailRow], {
    //     startY: 10,
    //     styles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
    //   });

    //   doc.autoTable(columnHeader, rows, { startY: 30 });
    //   doc.save(pdfName + ".pdf");
    //   this.setLoading(false);
    // },
    // expandItem(value) {
    //   if (!value.question.is_subjective) return;
    //   const index = this.expanded.indexOf(value);
    //   if (index === -1) this.expanded.push(value);
    //   else this.expanded.splice(index, 1);
    // },
  },
  created() {
    this.getTestDetails();
    this.getStudentResults();
  },
};
</script>

<style scoped>
.student_result {
  max-width: 90%;
  margin-left: 5%;
  margin-right: 5%;
}

.test-info {
  padding-right: 5%;
  margin-left: 5%;
  margin-top: 15px;
}

.align-end {
  text-align: end;
}
.test-description {
  padding: 10px;
  padding-left: 30px;
  border-top: 5px solid #009688;
  border-radius: 20px;
}

.pass {
  color: green;
}

.fail {
  color: red;
}
.test-details {
  word-break: break-word;
}
.v-expansion-panel::before {
  box-shadow: none !important;
}
.expanded-item-answer {
  padding: 10px;
}
.text-center {
  text-align: center;
}
</style>